import { ArrowRight, Pin, RotateCcw } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import {
  balanceAssign,
  eligibleStaff,
  getStaffDisplayName,
  getStaffInitials,
  getStaffRole,
  groupOwnedTasksByClientId,
  platformEligibleStaff,
} from '../../balanceAssign';
import {
  availableMinutes,
  committedMinutes,
  deadlineFeasibility,
  estMinutes,
  utilisation,
} from '../../capacity';
import { getTaskDisplayTitle } from '../../helpers';
import styles from './BulkAssignPreview.module.scss';

const PRIORITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };

const HORIZON_OPTIONS = [
  { value: 'day', label: 'Today' },
  { value: 'week', label: 'This week' },
];

const BulkAssignPreview = ({
  open,
  onClose,
  result,
  clients,
  allTasks,
  tasksById,
  activeTeam,
  onApply,
}) => {
  const [rows, setRows] = useState([]);
  const [viewMode, setViewMode] = useState('assignee');
  const [horizon, setHorizon] = useState('day');

  useEffect(() => {
    if (!open) return;
    const proposalRows = result.proposals
      .filter((p) => tasksById.has(p.taskId))
      .map((p) => ({
        taskId: p.taskId,
        task: tasksById.get(p.taskId),
        assigneeId: p.toClientId,
        included: true,
        pinned: false,
        manual: false,
        forced: false,
      }));
    const unassignableRows = result.unassignableTaskIds
      .filter((id) => tasksById.has(id))
      .map((id) => ({
        taskId: id,
        task: tasksById.get(id),
        assigneeId: null,
        included: false,
        pinned: false,
        manual: false,
        forced: false,
      }));
    setRows([...proposalRows, ...unassignableRows]);
    setViewMode('assignee');
    setHorizon('day');
  }, [open, result, tasksById]);

  const patch = (taskId, changes) =>
    setRows((prev) => prev.map((r) => (r.taskId === taskId ? { ...r, ...changes } : r)));

  const overrideAssignee = (taskId, newClientId) => {
    patch(taskId, {
      assigneeId: Number(newClientId),
      manual: true,
      pinned: true,
      forced: false,
      included: true,
    });
  };

  const forceAssign = (taskId, newClientId) => {
    patch(taskId, {
      assigneeId: Number(newClientId),
      manual: true,
      forced: true,
      included: true,
      pinned: true,
    });
  };

  const toggleInclude = (taskId) =>
    setRows((prev) => prev.map((r) => (r.taskId === taskId ? { ...r, included: !r.included } : r)));

  const togglePin = (row) => {
    if (row.manual) return;
    patch(row.taskId, { pinned: !row.pinned });
  };

  const clientsById = useMemo(() => {
    const m = new Map();
    clients.forEach((c) => m.set(c.id, c));
    return m;
  }, [clients]);

  const tasksByClientId = useMemo(
    () => groupOwnedTasksByClientId(allTasks, clients),
    [allTasks, clients],
  );

  const currentLoadByClientId = useMemo(() => {
    const load = {};
    clients.forEach((c) => {
      load[c.id] = committedMinutes(tasksByClientId.get(c.id) || [], horizon, getStaffRole(c));
    });
    return load;
  }, [clients, tasksByClientId, horizon]);

  const rebalance = () => {
    const seedLoad = { ...currentLoadByClientId };
    rows.forEach((r) => {
      if (r.pinned && r.included && r.assigneeId != null) {
        const client = clientsById.get(r.assigneeId);
        seedLoad[r.assigneeId] =
          (seedLoad[r.assigneeId] ?? 0) + estMinutes(r.task, getStaffRole(client));
      }
    });
    const pool = rows.filter((r) => r.included && !r.pinned).map((r) => r.task);
    const rebalanced = balanceAssign(pool, clients, seedLoad, activeTeam, horizon);
    const byTask = new Map(rebalanced.proposals.map((p) => [p.taskId, p.toClientId]));
    const unassignable = new Set(rebalanced.unassignableTaskIds);
    setRows((prev) =>
      prev.map((r) => {
        if (!r.included || r.pinned) return r;
        if (byTask.has(r.taskId)) {
          return { ...r, assigneeId: byTask.get(r.taskId), manual: false, forced: false };
        }
        if (unassignable.has(r.taskId)) return { ...r, assigneeId: null, included: false };
        return r;
      }),
    );
  };

  const movedTaskIds = useMemo(
    () => new Set(rows.filter((r) => r.included && r.assigneeId != null).map((r) => r.taskId)),
    [rows],
  );

  const assignedTasksByClientId = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      if (r.included && r.assigneeId != null && r.task) {
        if (!m.has(r.assigneeId)) m.set(r.assigneeId, []);
        m.get(r.assigneeId).push(r.task);
      }
    });
    return m;
  }, [rows]);

  const untouchedTasksByClientId = useMemo(() => {
    const m = new Map();
    tasksByClientId.forEach((tasks, clientId) => {
      m.set(
        clientId,
        tasks.filter((t) => !movedTaskIds.has(`${t.id}`)),
      );
    });
    return m;
  }, [tasksByClientId, movedTaskIds]);

  const relevantClients = useMemo(() => {
    const map = new Map();
    rows
      .filter((r) => r.included)
      .forEach((r) => {
        eligibleStaff(r.task, clients, activeTeam).forEach((c) => map.set(c.id, c));
        if (r.assigneeId != null) {
          const c = clientsById.get(r.assigneeId);
          if (c) map.set(c.id, c);
        }
      });
    return [...map.values()];
  }, [rows, clients, activeTeam, clientsById]);

  const workloadRows = useMemo(
    () =>
      relevantClients
        .map((c) => {
          const role = getStaffRole(c);
          const untouched = untouchedTasksByClientId.get(c.id) || [];
          const assigned = assignedTasksByClientId.get(c.id) || [];

          const baseline = committedMinutes(untouched, horizon, role);
          const delta = assigned.reduce((sum, task) => sum + estMinutes(task, role), 0);
          const projected = baseline + delta;
          const available = availableMinutes(
            horizon,
            c.dailyCapacityMinutes,
            c.weeklyCapacityMinutes,
          );
          const util = utilisation(projected, available);
          const over = projected > available;

          const feasibility = deadlineFeasibility(
            [...untouched, ...assigned],
            role,
            c.dailyCapacityMinutes,
          );
          const missedTaskIds = new Set(feasibility.filter((f) => f.willMiss).map((f) => f.taskId));
          const slaBreach = missedTaskIds.size > 0;

          return {
            client: c,
            baseline,
            delta,
            projected,
            available,
            util,
            over,
            slaBreach,
            missedTaskIds,
          };
        })
        .sort(
          (a, b) =>
            b.projected - a.projected ||
            getStaffDisplayName(a.client).localeCompare(getStaffDisplayName(b.client)),
        ),
    [relevantClients, untouchedTasksByClientId, assignedTasksByClientId, horizon],
  );

  const maxBar = Math.max(1, ...workloadRows.flatMap((r) => [r.projected, r.available]));
  const overCapCount = workloadRows.filter((r) => r.over).length;
  const slaBreachCount = workloadRows.filter((r) => r.slaBreach).length;
  const sharedBudget = workloadRows.every((r) => r.available === workloadRows[0]?.available)
    ? workloadRows[0]?.available
    : null;

  const allMissedTaskIds = useMemo(() => {
    const s = new Set();
    workloadRows.forEach((r) => r.missedTaskIds.forEach((id) => s.add(id)));
    return s;
  }, [workloadRows]);

  const includedAssigned = rows.filter((r) => r.included && r.assigneeId != null);
  const leftUnassignedCount = rows.filter((r) => !r.included).length;
  const distinctAssignees = new Set(includedAssigned.map((r) => r.assigneeId)).size;
  const unpinnedIncludedCount = rows.filter((r) => r.included && !r.pinned).length;

  const groups = useMemo(() => {
    if (viewMode === 'priority') {
      const sorted = [...rows].sort(
        (a, b) =>
          (PRIORITY_RANK[a.task?.priority || 'low'] ?? 3) -
          (PRIORITY_RANK[b.task?.priority || 'low'] ?? 3),
      );
      return [{ key: 'all', label: null, rows: sorted }];
    }
    const map = new Map();
    rows.forEach((r) => {
      const key = r.included && r.assigneeId != null ? r.assigneeId : '__none__';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return [...map.entries()]
      .sort(([a], [b]) => {
        if (a === '__none__') return 1;
        if (b === '__none__') return -1;
        return getStaffDisplayName(clientsById.get(a) || {}).localeCompare(
          getStaffDisplayName(clientsById.get(b) || {}),
        );
      })
      .map(([key, groupRows]) => ({
        key: `${key}`,
        label:
          key === '__none__' ? 'Not assigned' : getStaffDisplayName(clientsById.get(key) || {}),
        rows: groupRows,
      }));
  }, [rows, viewMode, clientsById]);

  const apply = () => {
    const proposals = includedAssigned.map((r) => {
      const ownerName = `${r.task?.owner || ''}`.trim().toLowerCase();
      const fromClientId = ownerName
        ? ([...clientsById.entries()].find(
            ([, c]) => getStaffDisplayName(c).toLowerCase() === ownerName,
          )?.[0] ?? null)
        : null;
      return {
        taskId: r.taskId,
        toClientId: r.assigneeId,
        toDisplayName: getStaffDisplayName(clientsById.get(r.assigneeId) || {}),
        fromClientId: fromClientId != null ? Number(fromClientId) : null,
      };
    });
    onApply(proposals);
  };

  if (!open) return null;

  const renderRow = (r) => {
    const eligible = eligibleStaff(r.task, clients, activeTeam);
    const hasEligible = eligible.length > 0;
    const outsideRole = r.assigneeId != null && !eligible.some((c) => c.id === r.assigneeId);
    const taskTitle = r.task ? getTaskDisplayTitle(r.task) : r.taskId;
    const willMissSla = allMissedTaskIds.has(r.taskId);
    const assigneeRole =
      r.assigneeId != null ? getStaffRole(clientsById.get(r.assigneeId)) : undefined;
    const mins = r.task ? estMinutes(r.task, assigneeRole) : 0;

    return (
      <div
        key={r.taskId}
        className={`${styles.assignRow} ${!r.included ? styles.assignRowDimmed : ''}`}
      >
        <input
          type="checkbox"
          className={styles.assignRowCheck}
          checked={r.included}
          onChange={() => toggleInclude(r.taskId)}
          aria-label="Include task"
        />
        <span className={styles.assignRowTitle}>{taskTitle}</span>
        <span className={styles.assignRowMinutes}>{mins}m</span>
        <span className={styles.assignRowFrom}>{r.task?.owner || 'Unassigned'}</span>
        <ArrowRight className={styles.assignRowArrow} strokeWidth={1.8} />
        {hasEligible ? (
          <select
            className={styles.assignRowSelect}
            value={r.assigneeId ?? ''}
            onChange={(e) => e.target.value && overrideAssignee(r.taskId, e.target.value)}
          >
            <option value="">Select assignee</option>
            {r.assigneeId != null && outsideRole && (
              <option value={r.assigneeId}>
                {getStaffDisplayName(clientsById.get(r.assigneeId) || {})} (out of role)
              </option>
            )}
            {eligible.map((c) => (
              <option key={c.id} value={c.id}>
                {getStaffDisplayName(c)}
              </option>
            ))}
          </select>
        ) : (
          <select
            className={styles.assignRowSelect}
            value={r.assigneeId ?? ''}
            onChange={(e) => e.target.value && forceAssign(r.taskId, e.target.value)}
          >
            <option value="">— force assign —</option>
            {platformEligibleStaff(r.task, clients).map((c) => (
              <option key={c.id} value={c.id}>
                {getStaffDisplayName(c)} · {getStaffRole(c)}
              </option>
            ))}
          </select>
        )}
        <div className={styles.assignRowMeta}>
          {willMissSla && <span className={styles.slaBadge}>will miss SLA</span>}
          {r.manual && <span className={styles.assignRowBadge}>manual</span>}
          {(r.forced || outsideRole) && (
            <span className={styles.assignRowWarning}>out of role</span>
          )}
        </div>
        <button
          type="button"
          className={`${styles.pinButton} ${r.pinned ? styles.pinButtonActive : ''}`}
          onClick={() => togglePin(r)}
          disabled={r.manual || !r.included}
          title={r.manual ? 'Manual rows are pinned' : r.pinned ? 'Unpin' : 'Pin'}
        >
          <Pin
            className={styles.pinIcon}
            strokeWidth={1.8}
            fill={r.pinned ? 'currentColor' : 'none'}
          />
        </button>
      </div>
    );
  };

  return (
    <div
      className={styles.overlay}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.card}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-assign-preview-title"
      >
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div id="bulk-assign-preview-title" className={styles.title}>
              Balance &amp; assign
            </div>
            <div className={styles.subtitle}>
              {includedAssigned.length > 0
                ? `Assigning ${includedAssigned.length} ${includedAssigned.length === 1 ? 'task' : 'tasks'} across ${distinctAssignees} ${distinctAssignees === 1 ? 'person' : 'people'}.`
                : 'No tasks included — adjust rows below.'}
              {leftUnassignedCount > 0 && (
                <span className={styles.subtitleMuted}>
                  {' '}
                  · {leftUnassignedCount} left unassigned
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            className={styles.rebalanceButton}
            onClick={rebalance}
            disabled={unpinnedIncludedCount === 0}
          >
            <RotateCcw className={styles.rebalanceIcon} strokeWidth={1.8} />
            Re-balance unpinned
          </button>
        </div>

        <div className={styles.body}>
          {workloadRows.length > 0 && (
            <div className={styles.workloadSection}>
              <div className={styles.workloadHeader}>
                <div className={styles.workloadHeaderLeft}>
                  <span className={styles.sectionLabel}>Projected workload</span>
                  <div className={styles.windowToggle}>
                    {HORIZON_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`${styles.windowToggleBtn} ${
                          horizon === option.value ? styles.windowToggleBtnActive : ''
                        }`}
                        onClick={() => setHorizon(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {sharedBudget != null && (
                    <span className={styles.workloadBudgetLabel}>budget {sharedBudget}m</span>
                  )}
                </div>
                <div className={styles.workloadBadges}>
                  {overCapCount > 0 && (
                    <span className={styles.overCapBadge}>{overCapCount} over capacity</span>
                  )}
                  {slaBreachCount > 0 && (
                    <span className={styles.slaBadge}>{slaBreachCount} will miss SLA</span>
                  )}
                </div>
              </div>
              <div className={styles.workloadList}>
                {workloadRows.map(
                  ({ client, baseline, delta, projected, available, util, over, slaBreach }) => {
                    const deltaClass = slaBreach
                      ? styles.workloadBarDeltaSla
                      : over
                        ? styles.workloadBarDeltaOver
                        : styles.workloadBarDelta;
                    const numClass = slaBreach
                      ? styles.workloadNumSla
                      : over
                        ? styles.workloadNumOver
                        : styles.workloadNum;

                    return (
                      <div key={client.id} className={styles.workloadRow}>
                        <div className={styles.workloadStaff}>
                          <span className={styles.workloadAvatar}>{getStaffInitials(client)}</span>
                          <div className={styles.workloadStaffInfo}>
                            <span className={styles.workloadStaffName}>
                              {getStaffDisplayName(client)}
                            </span>
                            <span className={styles.workloadStaffRole}>{getStaffRole(client)}</span>
                            {(over || slaBreach) && (
                              <div className={styles.workloadInlineBadges}>
                                {over && <span className={styles.overCapBadge}>over capacity</span>}
                                {slaBreach && (
                                  <span className={styles.slaBadge}>will miss SLA</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className={styles.workloadBarWrap}>
                          <div
                            className={styles.workloadBarCurrent}
                            style={{ width: `${(baseline / maxBar) * 100}%` }}
                          />
                          {delta > 0 && (
                            <div
                              className={deltaClass}
                              style={{ width: `${(delta / maxBar) * 100}%` }}
                            />
                          )}
                          <div
                            className={styles.workloadCapacityMarker}
                            style={{ left: `${(available / maxBar) * 100}%` }}
                          />
                        </div>
                        <div className={styles.workloadNumbers}>
                          <div>
                            <span className={numClass}>{projected}m</span>
                            {delta > 0 && (
                              <span className={styles.workloadNumDelta}> (+{delta})</span>
                            )}
                          </div>
                          <div className={styles.workloadUtilPct}>
                            {Math.round(util * 100)}% of {available}m
                          </div>
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          )}

          <div className={styles.assignSection}>
            <div className={styles.assignSectionHeader}>
              <span className={styles.sectionLabel}>Assignments ({rows.length})</span>
              <div className={styles.viewToggle}>
                <span className={styles.viewToggleLabel}>View:</span>
                <button
                  type="button"
                  className={`${styles.viewToggleBtn} ${viewMode === 'assignee' ? styles.viewToggleBtnActive : ''}`}
                  onClick={() => setViewMode('assignee')}
                >
                  By assignee
                </button>
                <button
                  type="button"
                  className={`${styles.viewToggleBtn} ${viewMode === 'priority' ? styles.viewToggleBtnActive : ''}`}
                  onClick={() => setViewMode('priority')}
                >
                  By priority
                </button>
              </div>
            </div>
            {rows.length === 0 ? (
              <div className={styles.emptyAssign}>No tasks to assign.</div>
            ) : (
              groups.map((group) => (
                <div key={group.key}>
                  {group.label != null && (
                    <div className={styles.groupHeader}>
                      <span className={styles.groupLabel}>{group.label}</span>
                      <span className={styles.groupCount}>
                        {group.rows.filter((r) => r.included && r.assigneeId != null).length}
                      </span>
                    </div>
                  )}
                  {group.rows.map(renderRow)}
                </div>
              ))
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.applyButton}
            disabled={includedAssigned.length === 0}
            onClick={apply}
          >
            {includedAssigned.length > 0
              ? `Apply ${includedAssigned.length} ${includedAssigned.length === 1 ? 'assignment' : 'assignments'}`
              : 'Apply assignments'}
          </button>
        </div>
      </div>
    </div>
  );
};

BulkAssignPreview.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  result: PropTypes.shape({
    proposals: PropTypes.arrayOf(
      PropTypes.shape({
        taskId: PropTypes.string,
        fromOwner: PropTypes.string,
        toClientId: PropTypes.number,
        toDisplayName: PropTypes.string,
      }),
    ),
    unassignableTaskIds: PropTypes.arrayOf(PropTypes.string),
  }),
  clients: PropTypes.arrayOf(PropTypes.object),
  allTasks: PropTypes.arrayOf(PropTypes.object),
  tasksById: PropTypes.instanceOf(Map),
  activeTeam: PropTypes.string,
  onApply: PropTypes.func,
};

export default BulkAssignPreview;
